import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ThunkConfig, ErrorField } from "store";
import { otcService } from "services/OTCService";
import {
  IOTCInitRequest,
  CustomerType,
  OperationType,
  OTCtype,
  CurrencyAssetResponse,
  convertCurrencyRequest,
  IOTCConvertResponse,
  acceptOTCResponse,
  IOTCStatusResponse,
} from "models/otc";
import {
  IndividualBuyCryptoForm,
  IndividualSellCryptoForm,
  BusinessBuyCryptoForm,
  BusinessSellCryptoForm,
} from "models/otcForms";

import { takeLeadingThunk } from "utils/takeLeadingThunk";

export const otcInit = createAsyncThunk<
  void,
  IOTCInitRequest,
  ThunkConfig<string>
>("otc/init", async (initData, { rejectWithValue, dispatch }) => {
  try {
    const {
      email,
      amount,
      from,
      to,
      integrationId,
      operationType,
      customerType,
      fromBlockchain,
      toBlockchain,
    } = initData;

    const response = await otcService.initExchange({
      email,
      amount,
      from,
      to,
      integrationId,
      operationType,
      customerType,
      fromBlockchain,
      toBlockchain,
    });

    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const checkPasswordStatus = createAsyncThunk<
  { id: string; email: string; hasPassword: boolean },
  { clientId: string },
  ThunkConfig<ErrorField[]>
>("otc/checkPasswordStatus", async (prop, { rejectWithValue }) => {
  try {
    const response = await otcService.getPasswordStatus(prop.clientId);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const individualBuyCryptoForm = createAsyncThunk<
  acceptOTCResponse,
  IndividualBuyCryptoForm,
  ThunkConfig<ErrorField[]>
>(
  "otc/accept_individual_fiat_to_crypto",
  async (initData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const id = state.exchange.exchangeData?.id;
      const { sender, recipient, createAccount } = initData;
      const data = {
        sender,
        recipient,
        createAccount,
        customerType: CustomerType.INDIVIDUAL,
        operationType: OperationType.FIAT_TO_CRYPTO,
        type: OTCtype.FIAT_TO_CRYPTO_INDIVIDUAL,
      };
      const response = await otcService.acceptExchange(data, id);
      localStorage.setItem("clientId", response.client.id);
      const status = await dispatch(
        getKYCKYBStatus({ id: response.id })
      ).unwrap();
      if (!status.isKycKybVerified) {
        dispatch(getKYCUrl({ clientId: response.client.id, id: response.id }));
      }
      return response;
    } catch (e: any) {
      return rejectWithValue(e.response.data.errors);
    }
  }
);

export const businessBuyCryptoForm = createAsyncThunk<
  acceptOTCResponse,
  BusinessBuyCryptoForm,
  ThunkConfig<ErrorField[]>
>(
  "otc/accept_business_fiat_to_crypto",
  async (initData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const id = state.exchange.exchangeData?.id;
      const { sender, recipient, createAccount } = initData;
      const data = {
        sender,
        recipient,
        createAccount,
        customerType: CustomerType.BUSINESS,
        operationType: OperationType.FIAT_TO_CRYPTO,
        type: OTCtype.FIAT_TO_CRYPTO_BUSINESS,
      };
      const response = await otcService.acceptExchange(data, id);
      localStorage.setItem("clientId", response.client.id);
      const status = await dispatch(
        getKYCKYBStatus({ id: response.id })
      ).unwrap();
      if (!status.isKycKybVerified) {
        dispatch(getKYBUrl({ clientId: response.client.id, id: response.id }));
      }
      return response;
    } catch (e: any) {
      return rejectWithValue(e.response.data.errors);
    }
  }
);

export const individualSellCryptoForm = createAsyncThunk<
  acceptOTCResponse,
  IndividualSellCryptoForm,
  ThunkConfig<ErrorField[]>
>(
  "otc/accept_individual_crypto_to_fiat",
  async (initData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const id = state.exchange.exchangeData?.id;
      const { sender, recipient, createAccount } = initData;
      const data = {
        sender,
        recipient,
        createAccount,
        customerType: CustomerType.INDIVIDUAL,
        operationType: OperationType.CRYPTO_TO_FIAT,
        type: OTCtype.CRYPTO_TO_FIAT_INDIVIDUAL,
      };
      const response = await otcService.acceptExchange(data, id);
      localStorage.setItem("clientId", response.client.id);
      const status = await dispatch(
        getKYCKYBStatus({ id: response.id })
      ).unwrap();
      if (!status.isKycKybVerified) {
        dispatch(getKYCUrl({ clientId: response.client.id, id: response.id }));
      }
      return response;
    } catch (e: any) {
      return rejectWithValue(e.response.data.errors);
    }
  }
);

export const businessSellCryptoForm = createAsyncThunk<
  acceptOTCResponse,
  BusinessSellCryptoForm,
  ThunkConfig<ErrorField[]>
>(
  "otc/accept_business_crypto_to_fiat",
  async (initData, { rejectWithValue, getState, dispatch }) => {
    try {
      const state = getState();
      const id = state.exchange.exchangeData?.id;
      const { sender, recipient, createAccount } = initData;
      const data = {
        sender,
        recipient,
        createAccount,
        customerType: CustomerType.BUSINESS,
        operationType: OperationType.CRYPTO_TO_FIAT,
        type: OTCtype.CRYPTO_TO_FIAT_BUSINESS,
      };
      const response = await otcService.acceptExchange(data, id);
      localStorage.setItem("clientId", response.client.id);
      const status = await dispatch(
        getKYCKYBStatus({ id: response.id })
      ).unwrap();
      if (!status.isKycKybVerified) {
        dispatch(getKYBUrl({ clientId: response.client.id, id: response.id }));
      }
      return response;
    } catch (e: any) {
      return rejectWithValue(e.response.data.errors);
    }
  }
);

export const getKYCUrl = createAsyncThunk<
  { formUrl: string },
  { clientId: string; id: number },
  ThunkConfig<ErrorField[]>
>("otc/kyc_url", async (ids, { rejectWithValue }) => {
  try {
    const response = await otcService.getKYCUrl(ids.clientId, ids.id);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});
export const getKYBUrl = createAsyncThunk<
  { formUrl: string },
  { clientId: string; id: number },
  ThunkConfig<ErrorField[]>
>("otc/kyb_url", async (ids, { rejectWithValue }) => {
  try {
    const response = await otcService.getKYBUrl(ids.clientId, ids.id);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});
export const getKYCKYBStatus = createAsyncThunk<
  IOTCStatusResponse,
  { id: number },
  ThunkConfig<ErrorField[]>
>("otc/kyc_kyb_status", async (ids, { rejectWithValue, dispatch }) => {
  try {
    const response = await otcService.getKYCKYBStatus(ids.id);
    if (
      response.isKycKybVerified &&
      response.needSof &&
      response.currentStatus == "KYC_CRYPTO_ADDRESS_VERIFIED"
    ) {
      dispatch(getProofOfFunds({ id: ids.id }));
    }
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const getProofOfFunds = createAsyncThunk<
  { formUrl: string },
  { id: number },
  ThunkConfig<ErrorField[]>
>("otc/proof_of_funds", async (ids, { rejectWithValue }) => {
  try {
    const clientId = localStorage.getItem("clientId");
    if (!clientId) {
      throw new Error("Client ID is null");
    }
    const response = await otcService.getProofOfFunds(clientId, ids.id);
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});
export const getCurrencyAsset = createAsyncThunk<
  CurrencyAssetResponse[],
  void,
  ThunkConfig<string>
>("otc/currency_asset", async (_, { rejectWithValue }) => {
  try {
    const response = await otcService.getCurrencyAsset();
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const convertCurrency = createAsyncThunk<
  IOTCConvertResponse,
  convertCurrencyRequest,
  ThunkConfig<string>
>("otc/convert_currency", async (payload, { rejectWithValue }) => {
  try {
    const { keepOrdering, ...data } = payload;
    const response = await otcService.convertCurrency(data);
    return { ...response, keepOrdering };
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});
export const getKYCAddressURL = createAsyncThunk<
  { formUrl: string },
  {
    clientId: string;
    requestId: string;
  },
  ThunkConfig<string>
>("otc/get_kyc_address", async (payload, { rejectWithValue }) => {
  try {
    const { clientId, requestId } = payload;
    const response = await otcService.getKYCURLAddress({
      clientId: clientId,
      requestId: requestId,
    });
    return response;
  } catch (e: any) {
    return rejectWithValue(e.response.data.errors);
  }
});

export const takeLeadingConvertCurrency = takeLeadingThunk(convertCurrency)();
export interface ExchangeSchema {
  from: {
    id: number;
    symbol: string;
    name: string;
    amount: number;
    type: string;
    currencies?: any;
    blockchain: string | null;
    maxAmount?: string;
    minAmount?: string;
  };
  to: {
    id: number;
    symbol: string;
    name: string;
    amount: number;
    type: string;
    currencies?: any;
    blockchain: string | null;
    maxAmount?: string;
    minAmount?: string;
  };
  email: string;
  exchangeData?: any;
  isErrorEmail: boolean;
  componentsName: string;
  acceptData?: any;
  KYCUrl?: { formUrl: string };
  KYBUrl?: { formUrl: string };
  isOpenPEPPopUp?: boolean;
  rate?: string;
  feePercent: number;
  isOpenServerErrorPopup?: boolean;
  errorHeader?: string;
  errorText?: string;
  isSupport?: boolean;
  proofOfFundsUrl?: { formUrl: string };
  verificationErorrHeader?: string;
  verificationErrorText?: string;
  isConvert?: boolean;
  KYCUrlForm? : string;
}

const initialState: ExchangeSchema = {
  from: {
    id: 1,
    symbol: "EUR",
    name: "Euro",
    amount: 0,
    type: "FIAT",
    blockchain: null,
  },
  to: {
    id: 2,
    symbol: "BTC",
    name: "Bitcoin",
    amount: 0,
    type: "CRYPTO",
    blockchain: null,
  },
  email: "",
  componentsName: "ExchangeCalculator",
  isErrorEmail: false,
  isOpenPEPPopUp: false,
  rate: "0",
  feePercent: 0,
  isOpenServerErrorPopup: false,
  isConvert: false,
  KYCUrlForm: ""
};

interface ErrorObject {
  field: string;
  message: string;
}

export const exchangeSlice = createSlice({
  name: "exchange",
  initialState,
  reducers: {
    setFromAmount: (state: ExchangeSchema, action) => {
      state.from = { ...state.from, amount: action.payload };
    },
    setToCurrency: (state: ExchangeSchema, action) => {
      state.to = { ...state.to, ...action.payload };
    },
    setToAmount: (state: ExchangeSchema, action) => {
      state.to = { ...state.to, amount: action.payload };
    },
    setFromCurrency: (state: ExchangeSchema, action) => {
      state.from = { ...state.from, ...action.payload };
    },
    changeRotation: (state: ExchangeSchema) => {
      const tempFrom = { ...state.from };
      const tempTo = { ...state.to };
      state.from = {
        ...tempTo,
      };
      state.to = {
        ...tempFrom,
      };
    },
    setEmail: (state: ExchangeSchema, action) => {
      return { ...state, email: action.payload };
    },
    setCloseErrorEmail: (state: ExchangeSchema) => {
      return { ...state, isErrorEmail: false };
    },
    setComponentsName: (state: ExchangeSchema, action) => {
      return { ...state, componentsName: action.payload };
    },
    setIsOpenPEPPopUp: (state: ExchangeSchema, action) => {
      return { ...state, isOpenPEPPopUp: action.payload };
    },
    setIsOpenServerErrorPopup: (state: ExchangeSchema, action) => {
      return { ...state, isOpenServerErrorPopup: action.payload };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(otcInit.fulfilled, (state, action) => {
      return {
        ...state,
        exchangeData: action.payload,
        componentsName: "FormContainer",
      };
    });
    builder.addCase(otcInit.rejected, (state, action) => {
      const errors: ErrorObject[] = action.payload as unknown as ErrorObject[];
      if (
        errors.some((error) => error.message === "EMAIL_ALREADY_USED_ERROR")
      ) {
        return {
          ...state,
          isErrorEmail: true,
          componentsName: "ExchangeCalculator",
        };
      } else {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });
    builder.addCase(individualBuyCryptoForm.fulfilled, (state, action) => {
      return {
        ...state,
        acceptData: action.payload,
        from: { ...state.from, amount: 0 },
        to: { ...state.to, amount: 0 },
        email: "",
      };
    });
    builder.addCase(individualBuyCryptoForm.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) =>
            error.message === "ADDRESS_IN_BLACKLIST" ||
            error.message === "INTERNAL_SERVER_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST"
              ? "contactSupport"
              : "serverError",
          isSupport:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST" ? true : false,
        };
      } else if (
        action.payload?.find((error) => error.field !== "walletAddress")
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });
    builder.addCase(individualSellCryptoForm.fulfilled, (state, action) => {
      return {
        ...state,
        acceptData: action.payload,
        from: { ...state.from, amount: 0 },
        to: { ...state.to, amount: 0 },
        email: "",
      };
    });
    builder.addCase(individualSellCryptoForm.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) =>
            error.message === "ADDRESS_IN_BLACKLIST" ||
            error.message === "INTERNAL_SERVER_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST"
              ? "contactSupport"
              : "serverError",
          isSupport:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST" ? true : false,
        };
      } else if (
        action.payload?.find((error) => error.field !== "walletAddress")
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });

    builder.addCase(getKYCUrl.fulfilled, (state, action) => {
      return {
        ...state,
        componentsName: "Verification",
        KYCUrl: action.payload,
      };
    });
    builder.addCase(getKYCUrl.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_HAS_VERIFICATION"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "clientHasVerification",
          isSupport: true,
          componentsName: "ExchangeCalculator",
        };
      } else {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });
    builder.addCase(getKYCAddressURL.fulfilled, (state, action) => {
      return {
        ...state,
        KYCUrlForm: action.payload.formUrl,
      };
    } );
    builder.addCase(businessBuyCryptoForm.fulfilled, (state, action) => {
      return {
        ...state,
        acceptData: action.payload,
        from: { ...state.from, amount: 0 },
        to: { ...state.to, amount: 0 },
        email: "",
      };
    });
    builder.addCase(businessBuyCryptoForm.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) =>
            error.message === "ADDRESS_IN_BLACKLIST" ||
            error.message === "INTERNAL_SERVER_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST"
              ? "contactSupport"
              : "serverError",
          isSupport:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST" ? true : false,
        };
      } else if (
        action.payload?.find((error) => error.field !== "walletAddress")
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });
    builder.addCase(businessSellCryptoForm.fulfilled, (state, action) => {
      return {
        ...state,
        acceptData: action.payload,
        from: { ...state.from, amount: 0 },
        to: { ...state.to, amount: 0 },
        email: "",
      };
    });
    builder.addCase(businessSellCryptoForm.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) =>
            error.message === "ADDRESS_IN_BLACKLIST" ||
            error.message === "INTERNAL_SERVER_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST"
              ? "contactSupport"
              : "serverError",
          isSupport:
            action.payload[0].message === "ADDRESS_IN_BLACKLIST" ? true : false,
        };
      } else if (
        action.payload?.find((error) => error.field !== "walletAddress")
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });
    builder.addCase(getCurrencyAsset.fulfilled, (state, action) => {
      const currentCurrencyFrom = action.payload.find(
        (i) => i.symbol == state.from.symbol
      );
      const currentCurrencyTo = action.payload.find(
        (i) => i.symbol == state.to.symbol
      );
      state.from.currencies = action.payload
        .filter((currency) => currency.type === state.from.type)
        .sort((a, b) => a.id - b.id);
      state.from = {
        ...state.from,
        blockchain: currentCurrencyFrom?.blockchain ?? null,
        minAmount: currentCurrencyFrom?.minAmount ?? undefined,
        maxAmount: currentCurrencyFrom?.maxAmount ?? undefined,
      };
      state.to.currencies = action.payload
        .filter((currency) => currency.type === state.to.type)
        .sort((a, b) => a.id - b.id);
      state.to = {
        ...state.to,
        blockchain: currentCurrencyTo?.blockchain ?? null,
        minAmount: currentCurrencyTo?.minAmount ?? undefined,
        maxAmount: currentCurrencyTo?.maxAmount ?? undefined,
      };
    });
    builder.addCase(getCurrencyAsset.rejected, (state) => {
      return {
        ...state,
        from: { ...state.from, currencies: [] },
        to: { ...state.to, currencies: [] },
      };
    });
    builder.addCase(getKYBUrl.fulfilled, (state, action) => {
      return {
        ...state,
        componentsName: "Verification",
        KYBUrl: action.payload,
      };
    });
    builder.addCase(getKYBUrl.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "CLIENT_HAS_VERIFICATION"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "clientHasVerification",
          isSupport: true,
          componentsName: "ExchangeCalculator",
        };
      }
    });
    builder.addCase(convertCurrency.pending, (state) => {
      return {
        ...state,
        isConvert: true,
      };
    });
    builder.addCase(convertCurrency.fulfilled, (state, action) => {
      const key = action.payload.keepOrdering ? "to" : "from";
      return {
        ...state,
        isConvert: false,
        rate: action.payload.rate,
        feePercent: action.payload.feePercent,
        [key]: {
          ...state[key as keyof typeof state],
          amount: action.payload.data,
        },
      };
    });

    /* temporary code commenting

    builder.addCase(getKYCKYBStatus.fulfilled, (state, action) => {
      if (action.payload.currentStatus == "KYC_KYB_IN_PROGRESS") {
        return {
          ...state,
          verificationErorrHeader: "verificationInProgress",
          verificationErrorText: "waitVerification",
          componentsName: "Verification_Error",
        };
      }
      if (action.payload.currentStatus == "KYC_CRYPTO_ADDRESS_IN_PROGRESS") {
        return {
          ...state,
          verificationErorrHeader: "verificationInProgress",
          verificationErrorText: "waitVerification",
          componentsName: "Verification_Error",
        };
      }
      if (
        action.payload.isKycKybVerified &&
        !action.payload.needSof &&
        action.payload.currentStatus == "KYC_CRYPTO_ADDRESS_VERIFIED"
      )
       {
        return {
          ...state,
          // componentsName: "FinishedPopup",
        };
      }
    });
    builder.addCase(getKYCKYBStatus.rejected, (state, action) => {
      if (
        action.payload?.find(
          (error: any) => error.message === "KYC_KYB_REJECTED_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "verificationRejected",
          isSupport: true,
          componentsName: "ExchangeCalculator",
        };
      }
      if (
        action.payload?.find(
          (error: any) =>
            error.message === "KYC_ADDRESS_INVALID_ERROR" ||
            error.message === "KYC_ADDRESS_IN_BLACKLIST_ERROR"
        )
      ) {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "invalidAddress",
          isSupport: true,
          componentsName: "ExchangeCalculator",
        };
      } else {
        return {
          ...state,
          isOpenServerErrorPopup: true,
          errorHeader: "anErrorOccurred",
          errorText: "serverError",
        };
      }
    });
    
     */
    builder.addCase(getProofOfFunds.fulfilled, (state, action) => {
      return {
        ...state,
        componentsName: "Verification_SOF",
        proofOfFundsUrl: action.payload,
      };
    });
    builder.addCase(getProofOfFunds.rejected, (state, action) => {
      return {
        ...state,
        isOpenServerErrorPopup: true,
        errorHeader: "anErrorOccurred",
        errorText: "serverError",
      };
    });
  },
});

export const { actions: exchangeActions } = exchangeSlice;
export const { reducer: exchangeReducer } = exchangeSlice;
